/**
 * Commodity price ticker along the bottom of the screen.
 *
 * Real prices from `/market/ticker`: last traded price, or the reference price
 * for anything that has not traded yet. Colour follows direction — green up, red
 * down, grey flat. The track is duplicated and shifted -50% so the scroll loops
 * seamlessly, and it pauses on hover so a price can actually be read.
 */

import { useEffect, useMemo, useState } from "react";
import type { JSX } from "react";
import { api, type TickerEntry } from "./api";

export function Ticker(): JSX.Element {
  const [entries, setEntries] = useState<TickerEntry[]>([]);

  useEffect(() => {
    let alive = true;
    const load = (): void => {
      api.ticker().then(
        (r) => {
          if (alive) setEntries(r.entries);
        },
        () => undefined,
      );
    };
    load();
    const t = setInterval(load, 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Two copies back to back; the -50% keyframe lands exactly on the seam.
  const doubled = useMemo(() => [...entries, ...entries], [entries]);

  if (entries.length === 0) {
    return (
      <div className="ticker">
        <div className="ticker-track" style={{ animation: "none" }}>
          <span className="tick faint">
            <span className="sym">Market</span>
            <span className="px">awaiting prices…</span>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="ticker" role="marquee" aria-label="Commodity prices">
      <div className="ticker-track">
        {doubled.map((e, i) => (
          <Tick key={`${e.commodity}-${i}`} entry={e} />
        ))}
      </div>
    </div>
  );
}

function Tick({ entry }: { entry: TickerEntry }): JSX.Element {
  const tone = entry.changePct > 0 ? "up" : entry.changePct < 0 ? "down" : "flat";
  const arrow = entry.changePct > 0 ? "▲" : entry.changePct < 0 ? "▼" : "=";
  return (
    <span className={`tick ${tone}`}>
      <span className="sym">{entry.commodity.replace(/_/g, " ")}</span>
      <span className="px">${entry.price}</span>
      <span className="chg">
        {arrow}
        {Math.abs(entry.changePct).toFixed(1)}%
      </span>
    </span>
  );
}
